import { FormatMoney } from '../../../../../../hooks/useMoney';
import LangEnum from '../../../../../../lang/LangEnum';
import { BusinessPolicyType } from '../../../../../../services/modules/business-policies/businessPolicies.types';
import {
  BusinessPolicyAstLogicalOperator,
  BusinessPolicyConditionNodeDTO,
  BusinessPolicyGrain,
  BusinessPolicyMeasureDTO,
  BusinessPolicyNodeKind,
} from '../../../../../../services/modules/business-policies/businessPolicyCondition.types';
import {
  BusinessPolicyGroupResultDTO,
  BusinessPolicyNodeOutcome,
  BusinessPolicyViolationDTO,
} from '../../../../../../services/modules/business-policies/businessPolicyViolation.types';
import { buildOrderProductUniqueIdentifier } from '../../../../../../utils/getOrderProductUniqueIdentifier';
import { businessPolicyNumberOperatorSymbol } from '../../../../../business-policies/business-policy.options';
import {
  MEASURE_CAPABILITIES,
  findMeasureKey,
} from '../../../../../business-policies/domain/businessPolicyCapabilities';
import { BusinessPolicyValueInput } from '../../../../../business-policies/domain/businessPolicyCapabilities.types';
import {
  isDnfConditionTree,
  listComparisonNodes,
  toRightFactorPercent,
} from '../../../../../business-policies/domain/conditionTree.utils';
import { AvailabilityEnum } from '../../../../../products/ProductTypes';
import { TranslateType } from '../../../../../translation/translationContext.types';
import { OrderError } from '../../../sections/OrderEditSections.types';
import {
  BuildComparisonLabelParams,
  BusinessPolicyCriterionStatus,
  BusinessPolicyItemViolationView,
  BusinessPolicyViolationBranchView,
  BusinessPolicyViolationCriterionView,
  BusinessPolicyViolationGroupView,
  BusinessPolicyViolationItem,
  BusinessPolicyViolationMeasure,
  BusinessPolicyViolationView,
  DnfBranch,
  GetBusinessPolicyViolationViewsParams,
  ToBusinessPolicyViolationViewParams,
  ToCriterionViewParams,
  ViolationFormatters,
} from './businessPolicyViolation.types';

export const businessPolicyErrorMessageCodes = [
  LangEnum.ORDER_RULE_BUSINESS_POLICY_BLOCK_RULE,
  LangEnum.ORDER_RULE_ORDER_BUSINESS_POLICY_CRITERIA_VOUCHER_RULE,
  LangEnum.ORDER_RULE_ORDER_BUSINESS_POLICY_CRITERIA_VOUCHER_APPLY_AGAIN_RULE,
  LangEnum.ORDER_RULE_ORDER_BUSINESS_POLICY_USED_VOUCHER_PER_CUSTOMER,
  LangEnum.ORDER_RULE_ORDER_BUSINESS_POLICY_VOUCHER_INACTIVE_RULE,
  LangEnum.ORDER_RULE_ORDER_BUSINESS_POLICY_VOUCHER_EXPIRED_RULE,
  LangEnum.ORDER_RULE_ORDER_BUSINESS_POLICY_PAYMENT_METHOD_RULE,
];

export const isBusinessPolicyError = ({ messageCode }: Pick<OrderError, 'messageCode'>) =>
  businessPolicyErrorMessageCodes.includes(messageCode);

const FIRING_OUTCOME_BY_TYPE: Record<BusinessPolicyType, BusinessPolicyNodeOutcome> = {
  [BusinessPolicyType.ORDER_RESTRICTION]: BusinessPolicyNodeOutcome.SATISFIED,
  [BusinessPolicyType.ORDER_WARNING]: BusinessPolicyNodeOutcome.SATISFIED,
  [BusinessPolicyType.ORDER_PRODUCTS_RESTRICTION]: BusinessPolicyNodeOutcome.SATISFIED,
  [BusinessPolicyType.ORDER_VOUCHER]: BusinessPolicyNodeOutcome.NOT_SATISFIED,
  [BusinessPolicyType.RELEASE_PAYMENT_METHODS]: BusinessPolicyNodeOutcome.NOT_SATISFIED,
  [BusinessPolicyType.ORDER_ITEM_DISCOUNT]: BusinessPolicyNodeOutcome.NOT_SATISFIED,
  [BusinessPolicyType.CASHBACK]: BusinessPolicyNodeOutcome.NOT_SATISFIED,
};

const AVAILABILITY_SHORT_LABEL: Record<AvailabilityEnum, LangEnum> = {
  [AvailabilityEnum.READY_FOR_DELIVERY]: LangEnum.GLOBAL_READY_FOR_DELIVERY_SHORT,
  [AvailabilityEnum.INFINITE_PRE_ORDER]: LangEnum.GLOBAL_PRE_ORDER_SHORT,
  [AvailabilityEnum.MATERIAL_PRE_ORDER]: LangEnum.GLOBAL_MATERIAL_PRE_ORDER_SHORT,
  [AvailabilityEnum.FUTURE_STOCK]: LangEnum.GLOBAL_FUTURE_STOCK_SHORT,
  [AvailabilityEnum.EMPTY]: LangEnum.GLOBAL_EMPTY,
};

const getCriterionStatus = (
  outcome: BusinessPolicyNodeOutcome | undefined,
  firingOutcome: BusinessPolicyNodeOutcome,
): BusinessPolicyCriterionStatus => {
  if (!outcome || outcome === BusinessPolicyNodeOutcome.IGNORED) {
    return BusinessPolicyCriterionStatus.IGNORED;
  }

  return outcome === firingOutcome
    ? BusinessPolicyCriterionStatus.VIOLATED
    : BusinessPolicyCriterionStatus.SATISFIED;
};

const listDnfBranches = (root: BusinessPolicyConditionNodeDTO): DnfBranch[] => {
  if (!isDnfConditionTree(root)) {
    return [{ node: root, criteria: listComparisonNodes(root), criteriaOperator: null }];
  }

  const isOrRoot =
    root.kind === BusinessPolicyNodeKind.LOGICAL &&
    root.operator === BusinessPolicyAstLogicalOperator.OR;

  return (isOrRoot ? root.children : [root]).map((node) => ({
    node,
    criteria: listComparisonNodes(node),
    criteriaOperator: BusinessPolicyAstLogicalOperator.AND,
  }));
};

export const formatCriterionValue = (
  value: number,
  valueInput: BusinessPolicyValueInput,
  formatMoney: FormatMoney,
) => (valueInput === BusinessPolicyValueInput.MONEY ? formatMoney(value) : String(value));

const getMeasureLabel = (measure: BusinessPolicyMeasureDTO, translate: TranslateType) => {
  const measureKey = findMeasureKey(measure);

  return measureKey
    ? translate(MEASURE_CAPABILITIES[measureKey].labelLangEnum)
    : translate(LangEnum.GLOBAL_CRITERIA);
};

const buildComparisonLabel = ({
  node,
  expected,
  valueInput,
  translate,
  formatMoney,
}: BuildComparisonLabelParams) => {
  const symbol = businessPolicyNumberOperatorSymbol[node.comparisonOperator];
  const { rightFactor } = node;

  if (node.rightMeasure) {
    const factor =
      rightFactor !== null && rightFactor !== 1
        ? `${toRightFactorPercent(rightFactor)}% ${translate(LangEnum.GLOBAL_OF)} `
        : '';

    return `${symbol} ${factor}${getMeasureLabel(node.rightMeasure, translate)}`;
  }

  const value = expected ?? node.rightConstant;

  return value == null
    ? symbol
    : `${symbol} ${formatCriterionValue(value, valueInput, formatMoney)}`;
};

const toGroupView = (
  group: BusinessPolicyGroupResultDTO,
  firingOutcome: BusinessPolicyNodeOutcome,
): BusinessPolicyViolationGroupView => ({
  productColorId: group.productColorId,
  productColorCode: group.productColorCode,
  availability: group.availability,
  deliveryWindowName: group.deliveryWindowName,
  itemKey: group.availability
    ? buildOrderProductUniqueIdentifier({
        productColorId: group.productColorId,
        availability: group.availability,
        deliveryWindowId: group.deliveryWindowId ?? null,
      })
    : undefined,
  expected: group.expected,
  reached: group.reached,
  isViolated:
    firingOutcome === BusinessPolicyNodeOutcome.SATISFIED ? group.satisfied : !group.satisfied,
});

const compareGroups = (
  first: BusinessPolicyViolationGroupView,
  second: BusinessPolicyViolationGroupView,
) =>
  Number(second.isViolated) - Number(first.isViolated) ||
  first.productColorCode.localeCompare(second.productColorCode) ||
  (first.deliveryWindowName ?? '').localeCompare(second.deliveryWindowName ?? '');

const toMeasureView = (left: BusinessPolicyMeasureDTO, translate: TranslateType) => {
  const measureKey = findMeasureKey(left);

  return {
    measureLabel: getMeasureLabel(left, translate),
    valueInput: measureKey
      ? MEASURE_CAPABILITIES[measureKey].valueInput
      : BusinessPolicyValueInput.UNIT,
    isGrouped: left.grain !== BusinessPolicyGrain.ORDER,
  };
};

const toCriterionView = ({
  node,
  evaluation,
  firingOutcome,
  translate,
  formatMoney,
}: ToCriterionViewParams): BusinessPolicyViolationCriterionView => {
  const measure = toMeasureView(node.left, translate);
  const expected = evaluation?.expected ?? null;

  return {
    nodeId: node.id ?? '',
    status: getCriterionStatus(evaluation?.outcome, firingOutcome),
    ...measure,
    comparisonLabel: buildComparisonLabel({
      node,
      expected,
      valueInput: measure.valueInput,
      translate,
      formatMoney,
    }),
    expected,
    reached: evaluation?.reached ?? null,
    groups: (evaluation?.groups ?? [])
      .map((group) => toGroupView(group, firingOutcome))
      .sort(compareGroups),
  };
};

const toBusinessPolicyViolationView = ({
  violation,
  messageCode,
  translate,
  formatMoney,
}: ToBusinessPolicyViolationViewParams): BusinessPolicyViolationView => {
  const firingOutcome = FIRING_OUTCOME_BY_TYPE[violation.businessPolicy.type];
  const evaluationByNodeId = new Map(
    violation.evaluation.nodes.map((evaluation) => [evaluation.nodeId, evaluation]),
  );

  const toBranchView = ({
    node,
    criteria,
    criteriaOperator,
  }: DnfBranch): BusinessPolicyViolationBranchView => {
    const criteriaViews = criteria.map((criterion) =>
      toCriterionView({
        node: criterion,
        evaluation: criterion.id ? evaluationByNodeId.get(criterion.id) : undefined,
        firingOutcome,
        translate,
        formatMoney,
      }),
    );
    const branchEvaluation = node.id ? evaluationByNodeId.get(node.id) : undefined;
    const isViolated = branchEvaluation
      ? getCriterionStatus(branchEvaluation.outcome, firingOutcome) ===
        BusinessPolicyCriterionStatus.VIOLATED
      : criteriaViews.some(
          (criterion) => criterion.status === BusinessPolicyCriterionStatus.VIOLATED,
        );

    return { criteria: criteriaViews, criteriaOperator, isViolated };
  };

  return {
    businessPolicy: violation.businessPolicy,
    messageCode,
    branches: violation.rewards.flatMap((reward) =>
      listDnfBranches(reward.condition).map(toBranchView),
    ),
  };
};

const isViolationList = (extra: OrderError['extra']): extra is BusinessPolicyViolationDTO[] =>
  Array.isArray(extra);

export const getBusinessPolicyViolationViews = ({
  errors,
  translate,
  formatMoney,
}: GetBusinessPolicyViolationViewsParams): BusinessPolicyViolationView[] =>
  errors.filter(isBusinessPolicyError).flatMap((error) =>
    isViolationList(error.extra)
      ? error.extra.map((violation) =>
          toBusinessPolicyViolationView({
            violation,
            messageCode: error.messageCode,
            translate,
            formatMoney,
          }),
        )
      : [],
  );

export const getCriterionLabel = ({
  measureLabel,
  comparisonLabel,
}: BusinessPolicyViolationMeasure) => `${measureLabel} ${comparisonLabel}`;

export const listViolatedCriteria = (violation: BusinessPolicyViolationView) =>
  violation.branches
    .filter((branch) => branch.isViolated)
    .flatMap((branch) =>
      branch.criteria.filter(
        (criterion) => criterion.status === BusinessPolicyCriterionStatus.VIOLATED,
      ),
    );

export const hasSingleCriterion = ({ branches }: BusinessPolicyViolationView) =>
  branches.length === 1 && branches[0].criteria.length === 1;

const countViolatedGroups = (criterion: BusinessPolicyViolationCriterionView) =>
  criterion.groups.filter((group) => group.isViolated).length;

export const getCriterionValueLabel = (
  criterion: BusinessPolicyViolationCriterionView,
  { translate, formatMoney }: ViolationFormatters,
) => {
  if (criterion.status === BusinessPolicyCriterionStatus.IGNORED) {
    return '';
  }

  if (criterion.isGrouped) {
    return translate(LangEnum.BUSINESS_POLICY_VIOLATION_ITEMS_OUT_OF_CRITERION, {
      quantity: countViolatedGroups(criterion),
    });
  }

  if (criterion.reached == null || criterion.expected == null) {
    return '';
  }

  return `${formatCriterionValue(criterion.reached, criterion.valueInput, formatMoney)} / ${formatCriterionValue(criterion.expected, criterion.valueInput, formatMoney)}`;
};

export const getViolationItemLabel = (
  item: BusinessPolicyViolationItem,
  translate: TranslateType,
) => {
  if (!item.availability) {
    return item.productColorCode;
  }

  const availability = translate(AVAILABILITY_SHORT_LABEL[item.availability]);
  const delivery =
    item.availability === AvailabilityEnum.READY_FOR_DELIVERY
      ? translate(LangEnum.GLOBAL_READY_FOR_DELIVERY)
      : item.deliveryWindowName;

  return `${item.productColorCode} (${availability}: ${delivery || '-'})`;
};

export const filterViolationsByItem = (
  violations: BusinessPolicyViolationView[],
  itemViolations: BusinessPolicyItemViolationView[],
) =>
  violations.filter(({ businessPolicy }) =>
    itemViolations.some((itemViolation) => itemViolation.businessPolicy.id === businessPolicy.id),
  );

const listItemViolations = (
  violation: BusinessPolicyViolationView,
): BusinessPolicyItemViolationView[] => {
  const isSingleCriterion = hasSingleCriterion(violation);

  return listViolatedCriteria(violation).flatMap((criterion) =>
    criterion.groups.flatMap((group) => {
      if (!group.isViolated || !group.itemKey) {
        return [];
      }

      return {
        businessPolicy: violation.businessPolicy,
        messageCode: violation.messageCode,
        itemKey: group.itemKey,
        item: {
          productColorId: group.productColorId,
          productColorCode: group.productColorCode,
          availability: group.availability,
          deliveryWindowName: group.deliveryWindowName,
        },
        criterion: isSingleCriterion
          ? {
              measureLabel: criterion.measureLabel,
              comparisonLabel: criterion.comparisonLabel,
              valueInput: criterion.valueInput,
              expected: group.expected,
              reached: group.reached,
            }
          : undefined,
      };
    }),
  );
};

export const indexViolationsByItem = (
  violations: BusinessPolicyViolationView[],
): Map<string, BusinessPolicyItemViolationView[]> => {
  const byItemKey = new Map<string, BusinessPolicyItemViolationView[]>();

  violations.flatMap(listItemViolations).forEach((itemViolation) => {
    const itemViolations = byItemKey.get(itemViolation.itemKey) ?? [];
    const isIndexed = itemViolations.some(
      ({ businessPolicy }) => businessPolicy.id === itemViolation.businessPolicy.id,
    );

    if (!isIndexed) {
      byItemKey.set(itemViolation.itemKey, [...itemViolations, itemViolation]);
    }
  });

  return byItemKey;
};
